import { Controller } from "@hotwired/stimulus"
import { submitGetFormTrimmed } from "../utils/get_form_submit"

/**
 * Auto-submit GET forms (trim empty params). Often used on wrapper divs inside a form;
 * Stimulus targets cannot reference an ancestor, so resolveForm() uses the DOM form-association
 * APIs (.form, closest("form")) — not arbitrary querySelector.
 *
 * Optional _source: set data-form-submit-source-value on the same controller scope and add
 * a hidden input with data-form-submit-target="sourceField" inside the form.
 */
export default class extends Controller {
  static values = { source: String }
  static targets = ["sourceField"]

  submit() {
    const form = this.resolveForm(this.element)
    if (!form) return
    this.applyOptionalSourceParam()
    submitGetFormTrimmed(form)
  }

  /**
   * For <input type="search"> when the value is already empty (e.g. Enter after clear).
   * Does not defer — use deferSubmitIfSearchCleared for the native clear (X) control.
   */
  submitIfSearchCleared(event) {
    const field = event.currentTarget
    if (!(field instanceof HTMLInputElement) || field.value !== "") return
    const form = this.resolveForm(field)
    if (!form) return
    this.applyOptionalSourceParam()
    submitGetFormTrimmed(form)
  }

  /**
   * <input type="search"> fires `search` when the native clear (X) is used. WebKit clears the
   * value after the event, so we defer one tick then submit if empty (same trimmed GET behavior).
   * +event.currentTarget+ is the input (even when the controller sits on an ancestor form).
   */
  deferSubmitIfSearchCleared(event) {
    const field = event.currentTarget
    if (!(field instanceof HTMLInputElement)) return

    setTimeout(() => {
      if (field.value !== "") return
      const form = this.resolveForm(field)
      if (!form) return
      this.applyOptionalSourceParam()
      submitGetFormTrimmed(form)
    }, 0)
  }

  resolveForm(el) {
    if (el instanceof HTMLFormElement) return el
    if ("form" in el && el.form instanceof HTMLFormElement) return el.form
    if (el instanceof Element) return el.closest("form")
    return null
  }

  applyOptionalSourceParam() {
    if (!this.hasSourceValue || !this.sourceValue || !this.hasSourceFieldTarget) return
    this.sourceFieldTarget.value = this.sourceValue
  }
}
